import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import CardImage from 'src/components/CardImage'
import useCommon from 'src/hooks/useCommon'
import useInView from 'src/hooks/useInView'
import { ILocation, IPhoto } from 'src/types/graphql'
import { getCoverPhoto, getCoverPhotoUrl } from 'src/utils/coverPhoto'
import { orderGalleryPhotos } from 'src/utils/galleryOrder'

interface ImageCarouselProps {
  location: ILocation
  className?: string
  isHovered: boolean
  handleMouseEnter: () => void
  handleMouseLeave: (buttons?: boolean) => () => void
  onClick?: () => void
  priority?: boolean
  /**
   * When true, renders a single fill-the-parent image carousel instead of
   * the dual mobile (aspect-[8/10]) / desktop (aspect-[100/66]) blocks. Used
   * by horizontal search cards where the parent's `h-full w-full` already
   * constrains the image area — the per-orientation aspect ratios would
   * otherwise leave empty space below the image. Also drops the
   * `rounded-t-2xl` corners (parent decides rounding).
   */
  horizontal?: boolean
  /** Called when the user advances the carousel (arrow or swipe). */
  onAdvance?: () => void
}

const MAX_DOTS = 5

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  location,
  className = '',
  isHovered,
  handleMouseEnter,
  handleMouseLeave,
  onClick,
  priority = false,
  horizontal = false,
  onAdvance
}) => {
  const cover = getCoverPhoto(location)
  const [activeIndex, setActiveIndex] = useState(0)

  // Cover first (server-resolved, config-aware), then the pool without the
  // cover's duplicate — see src/utils/galleryOrder.ts.
  const [photos] = useState<IPhoto[]>(() =>
    orderGalleryPhotos({
      photos: location.photos,
      cover,
      activeConfiguration: location.defaultConfiguration
    })
  )

  // Embla measures every slide (offsetTop/offsetWidth) the moment it gets its
  // viewport node — `createEngine()` runs before the `active` check inside
  // `activate()`, so `active: false` does NOT skip it. Twelve cards
  // initialising inside the same commit meant twelve forced layouts of a
  // ~3.7k-node page: 236 ms of pure reflow at 4× CPU on the city page
  // (2026-09-25 trace, embla `measure`), and on desktop it ran against the
  // `md:hidden` mobile wrapper for nothing. Hand Embla its node only once the
  // carousel is near the viewport (latched by useInView), so off-screen cards
  // cost zero until they scroll in. Until then the overflow-hidden flex row
  // already paints slide 1 — identical to an initialised carousel at index 0.
  const viewportHostRef = useRef<HTMLDivElement>(null)
  const isNearViewport = useInView(viewportHostRef, {
    threshold: 0,
    rootMargin: '300px'
  })
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const emblaViewportRef = isNearViewport ? emblaRef : undefined

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap())
      onAdvance?.()
    }
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onAdvance])

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) {
        setActiveIndex(index)
        return
      }
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const handlePrevClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1))
  }

  const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setActiveIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0))
  }

  const handleEmblaPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    emblaApi?.scrollPrev()
  }

  const handleEmblaNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    emblaApi?.scrollNext()
  }

  // Custom pagination render function for mobile
  const renderCustomPagination = () => {
    if (!photos.length) return null

    const totalSlides = photos.length
    let dots: number[] = []

    if (totalSlides <= MAX_DOTS) {
      dots = Array.from({ length: totalSlides }, (_, i) => i)
    } else {
      if (activeIndex <= 1) {
        dots = [0, 1, 2, -1, totalSlides - 1]
      } else if (activeIndex >= totalSlides - 2) {
        dots = [0, -1, totalSlides - 3, totalSlides - 2, totalSlides - 1]
      } else {
        dots = [0, -1, activeIndex, -1, totalSlides - 1]
      }
    }

    return (
      <div
        className="absolute inset-x-0 bottom-2 z-20 flex justify-center gap-1.5"
        key="pagination"
      >
        {dots.map((index, i) => (
          <button
            key={`dot-${i}-${index}`}
            onClick={(e) => {
              e.stopPropagation()
              if (index >= 0) scrollTo(index)
            }}
            className={`h-2 transition-all duration-200 ${
              index === -1
                ? 'w-2 cursor-default rounded-full bg-white-500/40'
                : index === activeIndex
                  ? 'w-5 rounded-full bg-white-500'
                  : 'w-2 rounded-full bg-white-500/70'
            }`}
            aria-label={
              index === -1 ? 'More images' : `Go to image ${index + 1}`
            }
          />
        ))}
      </div>
    )
  }

  const { locMap, showSize } = useCommon()
  const sizeText = showSize(location, true)
  const locType = locMap(location)

  if (!photos.length) {
    return (
      <div
        className={`relative w-full overflow-hidden ${horizontal ? 'h-full' : 'rounded-t-2xl'} ${className}`}
      >
        <CardImage
          src={getCoverPhotoUrl(location)}
          alt={`${locType} ${sizeText} ${location.id}`}
          eager={priority}
        />
      </div>
    )
  }

  // Horizontal card mode — single carousel block that fills the parent
  // container instead of applying its own aspect ratio. Uses Embla so swipe
  // still works on touch devices. Desktop gets hover-revealed chevron
  // buttons (md:+ only) because finger-swipe on a trackpad / mouse is
  // unintuitive; mobile relies on native swipe.
  if (horizontal) {
    return (
      <div
        ref={viewportHostRef}
        className={`relative h-full w-full overflow-hidden ${className}`}
      >
        <div
          ref={emblaViewportRef}
          className="h-full w-full overflow-hidden"
          onClick={onClick}
        >
          <div className="flex h-full tap-highlight-transparent select-none">
            {photos.map((photo, index) => (
              <div
                className="relative h-full min-w-0 shrink-0 basis-full"
                key={`slide-${index}`}
              >
                <CardImage
                  src={photo.url ?? ''}
                  alt={`${locType} ${sizeText} ${location.id}`}
                  eager={priority && index === 0}
                />
              </div>
            ))}
          </div>
        </div>

        {photos.length > 1 && (
          <>
            <button
              type="button"
              className={`nav-button absolute top-1/2 left-1.5 z-50 hidden -translate-y-1/2 rounded-full bg-black-500/50 p-1 transition-opacity duration-200 hover:bg-black-500/70 md:block ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={handleEmblaPrev}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave(true)}
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="size-3.5 text-white-500 drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)]" />
            </button>
            <button
              type="button"
              className={`nav-button absolute top-1/2 right-1.5 z-50 hidden -translate-y-1/2 rounded-full bg-black-500/50 p-1 transition-opacity duration-200 hover:bg-black-500/70 md:block ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={handleEmblaNext}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave(true)}
              aria-label="Next image"
            >
              <ChevronRightIcon className="size-3.5 text-white-500 drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)]" />
            </button>
            {renderCustomPagination()}
          </>
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className={`relative w-full overflow-hidden rounded-t-2xl ${className} hidden md:block`}
      >
        <div className="relative aspect-[100/66] w-full">
          <CardImage
            src={photos[activeIndex].url ?? ''}
            alt={`${locType} ${sizeText} ${location.id}`}
            eager={priority && activeIndex === 0}
          />
        </div>

        <button
          className={`nav-button absolute top-1/2 left-2 z-50 -translate-y-1/2 rounded-full bg-black-500/50 p-1.5 transition-opacity duration-200 hover:bg-black-500/70 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handlePrevClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave(true)}
        >
          <ChevronLeftIcon className="size-4 text-white-500 drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)]" />
        </button>
        <button
          className={`nav-button absolute top-1/2 right-2 z-50 -translate-y-1/2 rounded-full bg-black-500/50 p-1.5 transition-opacity duration-200 hover:bg-black-500/70 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleNextClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave(true)}
        >
          <ChevronRightIcon className="size-4 text-white-500 drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)]" />
        </button>
      </div>

      <div
        ref={viewportHostRef}
        className={`relative w-full overflow-hidden rounded-t-2xl ${className} md:hidden`}
      >
        <div
          ref={emblaViewportRef}
          className="aspect-[8/10] w-full overflow-hidden"
          onClick={onClick}
        >
          <div className="flex h-full tap-highlight-transparent select-none">
            {photos.map((photo, index) => (
              <div
                className="relative h-full min-w-0 shrink-0 basis-full"
                key={`slide-${index}`}
              >
                <CardImage
                  src={photo.url ?? ''}
                  alt={`${locType} ${sizeText} ${location.id}`}
                  eager={priority && index === 0}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Custom pagination outside Embla viewport for better control */}
        {photos.length > 1 && renderCustomPagination()}
      </div>
    </>
  )
}

export default ImageCarousel
